import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MembershipPlan,
  AppointmentPackage,
} from "../../types/wizard-demo.types";

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MembershipPlan | AppointmentPackage | null;
  type: "membership" | "appointment";
  brandColor?: string;
}

const QRCodePlaceholder: React.FC = () => {
  return (
    <div className="tw-w-32 tw-h-32 tw-bg-white tw-border-2 tw-border-gray-200 tw-rounded-lg tw-overflow-hidden tw-relative">
      {/* QR Code Background */}
      <div className="tw-absolute tw-inset-0 tw-bg-black tw-m-3">
        {/* QR Code Pattern */}
        <div
          className="tw-w-full tw-h-full"
          style={{
            background: `url("data:image/svg+xml,%3Csvg width='104' height='104' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='qr' width='8' height='8' patternUnits='userSpaceOnUse'%3E%3Crect width='4' height='4' fill='%23000'/%3E%3Crect x='4' y='4' width='4' height='4' fill='%23000'/%3E%3Crect x='0' y='4' width='4' height='4' fill='%23fff'/%3E%3Crect x='4' y='0' width='4' height='4' fill='%23fff'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='104' height='104' fill='url(%23qr)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Animated Scanner Line */}
      <motion.div
        className="tw-absolute tw-left-3 tw-right-3 tw-h-0.5 tw-bg-green-500 tw-z-20"
        style={{
          boxShadow:
            "0 0 10px rgba(34, 197, 94, 0.8), 0 0 20px rgba(34, 197, 94, 0.4)",
          top: 12,
        }}
        animate={{
          y: [0, 104, 0], // Move within the QR code area
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Corner brackets */}
      <div className="tw-absolute tw-top-2 tw-left-2 tw-w-4 tw-h-4 tw-border-l-2 tw-border-t-2 tw-border-green-500 tw-z-10"></div>
      <div className="tw-absolute tw-top-2 tw-right-2 tw-w-4 tw-h-4 tw-border-r-2 tw-border-t-2 tw-border-green-500 tw-z-10"></div>
      <div className="tw-absolute tw-bottom-2 tw-left-2 tw-w-4 tw-h-4 tw-border-l-2 tw-border-b-2 tw-border-green-500 tw-z-10"></div>
      <div className="tw-absolute tw-bottom-2 tw-right-2 tw-w-4 tw-h-4 tw-border-r-2 tw-border-b-2 tw-border-green-500 tw-z-10"></div>
    </div>
  );
};

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  isOpen,
  onClose,
  item,
  type,
  brandColor = "#3B82F6",
}) => {
  return (
    <AnimatePresence>
      {isOpen && item && (
        <motion.div
          key="purchase-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: "easeInOut",
          }}
          className="tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-40 tw-flex tw-items-center tw-justify-center tw-p-4 tw-z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.85, opacity: 0, y: 20 }}
            animate={{
              scale: 1,
              opacity: 1,
              y: 0,
              transition: {
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              },
            }}
            exit={{
              scale: 0.9,
              opacity: 0,
              y: -10,
              transition: {
                duration: 0.5,
                ease: [0.4, 0.0, 0.2, 1],
              },
            }}
            className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-max-w-xs tw-w-full tw-mx-auto tw-overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-pb-3">
              <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
                {item.name}
              </h2>
              <button
                onClick={onClose}
                className="tw-text-gray-400 tw-hover:tw-text-gray-600 tw-text-2xl tw-font-light tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-hover:tw-bg-gray-100"
              >
                ×
              </button>
            </div>

            {/* Details */}
            <div className="tw-px-4 tw-space-y-3">
              <div className="tw-flex tw-items-center tw-justify-between">
                <span className="tw-text-gray-600 tw-font-medium">Price</span>
                <span className="tw-text-xl tw-font-bold tw-text-gray-900">
                  {item.price}
                </span>
              </div>

              {type === "membership" ? (
                <>
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <span className="tw-text-gray-600 tw-font-medium">
                      Sessions expire
                    </span>
                    <span className="tw-text-gray-900 tw-font-medium">
                      After 1 {(item as MembershipPlan).duration}
                    </span>
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <span className="tw-text-gray-600 tw-font-medium">
                      Sessions
                    </span>
                    <span className="tw-text-gray-900 tw-font-medium">
                      {(item as MembershipPlan).features.includes(
                        "Unlimited Access",
                      ) ||
                      (item as MembershipPlan).features.includes(
                        "Unlimited Classes",
                      )
                        ? "Unlimited"
                        : (item as MembershipPlan).features[0] || "Standard"}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <span className="tw-text-gray-600 tw-font-medium">
                      Sessions expire
                    </span>
                    <span className="tw-text-gray-900 tw-font-medium">
                      After 3 months
                    </span>
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-between">
                    <span className="tw-text-gray-600 tw-font-medium">
                      Sessions
                    </span>
                    <span className="tw-text-gray-900 tw-font-medium">
                      {(item as AppointmentPackage).sessions}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* QR Code */}
            <div className="tw-flex tw-flex-col tw-items-center tw-py-6">
              <QRCodePlaceholder />
              <p className="tw-text-gray-700 tw-text-center tw-mt-4 tw-px-4 tw-font-medium">
                Scan QR code to complete purchase
                <br />
                on your phone.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="tw-flex tw-space-x-3 tw-p-2 tw-pt-0 tw-justify-between">
              <button
                onClick={onClose}
                className="tw-py-2 tw-px-4 tw-rounded-full tw-border-2"
                style={{
                  borderColor: brandColor,
                  color: brandColor,
                  backgroundColor: "transparent",
                }}
              >
                Back
              </button>
              <button
                onClick={onClose}
                className="tw-py-2 tw-px-4 tw-rounded-full tw-text-white tw-font-medium tw-transition-colors"
                style={{
                  backgroundColor: brandColor,
                }}
              >
                Done
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PurchaseModal;
